import { useState } from "react"
import { FightClient } from "@/domain/fight/client/FightClient"
import { WEIGHT_CLASS_PREREQUISITE_NOTICE } from "@/domain/fight/model/Fight"
import type {
  WeightClassChoicesState,
  WeightClassOption,
} from "@/domain/fight/model/Fight"

/**
 * 체급을 물을 때 필요한 값 — 선수 입력에 대회 id 를 더한 것이다
 *
 * 대회 id 를 인자로 받는 이유: 수정 화면은 신청을 불러온 **뒤에** 대회를 알게 되므로
 * 훅 생성 시점의 값으로는 늦다.
 */
interface WeightClassSubject {
  tournamentId: string
  key: string
  sport: string
  birthDate: string
  gender: string
}

function makeInitChoices(): WeightClassChoicesState {
  return {
    options: [],
    notice: WEIGHT_CLASS_PREREQUISITE_NOTICE,
    isLoading: false,
    isAsked: false,
  }
}

/**
 * 선수별 체급 선택지 훅 (신청 · 수정 화면 공용).
 *
 * 어느 체급을 고를 수 있는지는 **서버가** 정한다 — 부(초등 · 중등 · 고등 · 성인)가 대회 시작일 +
 * 생년월일에서 나오므로 학년 계산 규칙을 화면에 복제하지 않는다.
 * 체중은 선택지를 좁히지 않는다: 선수는 대회 당일에 체급을 맞춰 온다.
 */
export function useWeightClassChoices() {
  const [choicesByPlayer, setChoicesByPlayer] = useState<
    Record<string, WeightClassChoicesState>
  >({})

  function getChoices(playerKey: string): WeightClassChoicesState {
    return choicesByPlayer[playerKey] ?? makeInitChoices()
  }

  function putChoices(playerKey: string, choices: WeightClassChoicesState) {
    setChoicesByPlayer((current) => ({ ...current, [playerKey]: choices }))
  }

  /**
   * 선택지를 서버에 묻는다
   *
   * 미취학처럼 참가 자체가 안 되는 경우도 서버가 문구로 알려주므로, 제출까지 기다리지 않고
   * 그 자리에서 보인다.
   */
  async function loadChoices(
    subject: WeightClassSubject,
  ): Promise<WeightClassOption[]> {
    const isAskable = Boolean(
      subject.tournamentId &&
      subject.sport &&
      subject.birthDate &&
      subject.gender,
    )
    if (!isAskable) {
      putChoices(subject.key, makeInitChoices())
      return []
    }

    putChoices(subject.key, {
      options: [],
      notice: "",
      isLoading: true,
      isAsked: false,
    })

    try {
      const choices = await FightClient.getWeightClass({
        tournamentId: subject.tournamentId,
        sport: subject.sport,
        birthDate: subject.birthDate,
        gender: subject.gender,
      })
      const hasNoChoice = choices.options.length === 0

      putChoices(subject.key, {
        options: choices.options,
        notice: hasNoChoice
          ? `${choices.divisionLabel} 체급표가 아직 없습니다. 접수 후 운영자가 체급을 확정합니다.`
          : "",
        isLoading: false,
        isAsked: true,
      })

      return choices.options
    } catch (error) {
      putChoices(subject.key, {
        options: [],
        notice:
          error instanceof Error
            ? error.message
            : "체급을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.",
        isLoading: false,
        isAsked: false,
      })

      return []
    }
  }

  return { getChoices, loadChoices }
}

export type { WeightClassSubject }
